import { useState } from 'react'
import type { Report } from '@/api/types'
import { useUser } from '@/auth/useAuth'
import { reportKindLabel } from '@/i18n/labels'
import { fetchDownloadUrl } from '@/features/files/queries'
import { Button, Card, EmptyState, ErrorState, LoadingState } from '@/ui'
import { ReportCreateSheet } from './ReportCreateSheet'
import { useDeleteReport, useReports } from './queries'

function canDelete(report: Report, userId: string, role: string): boolean {
  return report.author_id === userId || role === 'ADMIN' || role === 'OWNER'
}

export function ReportsTab({ orderId }: { orderId: string }) {
  const user = useUser()
  const { data, isLoading, isError, error, refetch } = useReports(orderId)
  const del = useDeleteReport(orderId)
  const [createOpen, setCreateOpen] = useState(false)

  async function openFile(id: string) {
    try {
      const url = await fetchDownloadUrl(id)
      window.open(url, '_blank', 'noopener')
    } catch {
      /* surfaced via disabled UX; ignore */
    }
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button size="md" onClick={() => setCreateOpen(true)}>
          + Отчёт
        </Button>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title="Отчётов нет" hint="Добавьте фото-отчёт о ходе работ." />
      ) : (
        <div className="space-y-2">
          {data.items.map((r) => (
            <Card key={r.id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium text-tg-text">{reportKindLabel[r.kind]}</span>
                {canDelete(r, user.id, user.role) && (
                  <button
                    className="text-xs text-risk-red active:opacity-70"
                    onClick={() => del.mutate(r.id)}
                  >
                    Удалить
                  </button>
                )}
              </div>
              {r.comment && <p className="mt-1 text-sm text-tg-text">{r.comment}</p>}
              {r.file_ids.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.file_ids.map((fid, i) => (
                    <button
                      key={fid}
                      className="rounded-lg bg-tg-secondary-bg px-2 py-1 text-xs text-tg-link"
                      onClick={() => void openFile(fid)}
                    >
                      Файл {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <ReportCreateSheet orderId={orderId} open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
